// Google IMA (Interactive Media Ads) HTML5 SDK loader + VAST pre-roll runner.
//
// Design rule: ads must NEVER block content. Every failure mode — no ad tag
// configured, SDK fails to load, empty/errored VAST response, timeout — resolves
// the returned promise so the caller proceeds to play the content. The promise
// therefore never rejects.

const IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ima = any;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google?: { ima?: Ima };
  }
}

let sdkPromise: Promise<Ima | null> | null = null;

/** Inject the IMA SDK <script> once; resolves to google.ima (or null on failure). */
export function loadImaSdk(): Promise<Ima | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve(null);
  if (window.google?.ima) return Promise.resolve(window.google.ima);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<Ima | null>((resolve) => {
    const script = document.createElement('script');
    script.src = IMA_SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.google?.ima ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return sdkPromise;
}

export interface PrerollOptions {
  adTagUrl: string;
  /** Element IMA draws the ad UI into (overlays the content video). */
  adContainer: HTMLElement;
  /** The content <video> IMA needs a handle to for sizing + resume. */
  contentVideo: HTMLVideoElement;
  /** Give up and proceed to content after this long. Default 8s. */
  timeoutMs?: number;
}

/**
 * Play a single VAST pre-roll, then resolve. Resolves (never rejects) on ad
 * complete, skip, empty response, any ads error, or timeout — so content always
 * proceeds.
 */
export function playPreroll(opts: PrerollOptions): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      try {
        adsManager?.destroy?.();
      } catch {
        /* ignore */
      }
      resolve();
    };

    if (!opts.adTagUrl) return done();

    const timeout = window.setTimeout(done, opts.timeoutMs ?? 8000);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let adsManager: any = null;

    void loadImaSdk().then((ima) => {
      if (!ima) {
        window.clearTimeout(timeout);
        return done();
      }
      try {
        const display = new ima.AdDisplayContainer(opts.adContainer, opts.contentVideo);
        display.initialize(); // must happen in the user-gesture call stack
        const loader = new ima.AdsLoader(display);

        loader.addEventListener(
          ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (e: any) => {
            adsManager = e.getAdsManager(opts.contentVideo);
            const finish = (): void => {
              window.clearTimeout(timeout);
              done();
            };
            adsManager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, finish);
            adsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, finish);
            adsManager.addEventListener(ima.AdEvent.Type.COMPLETE, finish);
            adsManager.addEventListener(ima.AdEvent.Type.SKIPPED, finish);
            try {
              adsManager.init(opts.adContainer.clientWidth || 640, opts.adContainer.clientHeight || 360, ima.ViewMode.NORMAL);
              adsManager.start();
            } catch {
              finish();
            }
          },
        );
        // Empty/invalid VAST or network error → proceed.
        loader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, () => {
          window.clearTimeout(timeout);
          done();
        });

        const request = new ima.AdsRequest();
        request.adTagUrl = opts.adTagUrl;
        request.linearAdSlotWidth = opts.adContainer.clientWidth || 640;
        request.linearAdSlotHeight = opts.adContainer.clientHeight || 360;
        loader.requestAds(request);
      } catch {
        window.clearTimeout(timeout);
        done();
      }
    });
  });
}

/** The configured VAST tag URL, or '' when ads are disabled. */
export function adTagUrl(): string {
  return (import.meta.env.VITE_IMA_AD_TAG_URL as string | undefined) ?? '';
}
